import type { AreaId, BalanceId, PersonId } from '@shared/brand';
import type { CalendarDate } from '../value-objects/CalendarDate';
import type { Auditable } from './Organization';

/**
 * A person's vacation balance for one year, precomputed.
 *
 * This record exists because of the 500-person requirement. Deriving a balance
 * means summing the business days of every approved request for that person and
 * year; doing it in the client for a roster view would be 500 queries, and doing
 * it in one query would mean reading every request the company has ever filed.
 * A Cloud Function maintains this document on each request transition instead,
 * so every screen that shows a balance is a single indexed read.
 *
 * The trade-off is that the counters can lag by the duration of a function
 * invocation. That is acceptable: nothing authorises off this record. The
 * concurrency cap and the entitlement check are re-derived from the source
 * requests inside the callable that approves, so a stale counter can never
 * approve days a person does not have.
 */
export interface Balance extends Auditable {
  /** `${personId}_${year}` — deterministic, so the function can upsert blind. */
  readonly id: BalanceId;
  readonly personId: PersonId;
  readonly personName: string;
  readonly areaId: AreaId;
  readonly year: number;
  /** Days granted by the seniority table for this person's tenure. */
  readonly entitledDays: number;
  /**
   * Days of this period already spent **before the portal existed**.
   *
   * The migration figure, and the reason the imported balances mean anything.
   * Every other counter here is maintained from requests filed in this system,
   * and the authoritative affordability check recomputes from those requests
   * rather than trusting the counters — so a person imported with two days left
   * would have read as having their whole entitlement free, and the server would
   * have agreed.
   *
   * Set once at import and then left alone. It is not a counter: nothing
   * increments it, and the yearly rollover is the only thing that rewrites it —
   * carrying forward an overdraft, which is how somebody who took more days than
   * they had starts the next period seven days short.
   */
  readonly openingUsedDays: number;
  /**
   * La parte de `openingUsedDays` que es deuda del periodo anterior: días que la
   * persona tomó de más y que se le descuentan al abrir éste. El resto son días
   * gastados antes de que existiera el portal. Cero en todo lo que se importó.
   */
  readonly carriedDebtDays: number;
  /** Business days on requests already taken. */
  readonly takenDays: number;
  /** Business days on approved requests whose dates are still in the future. */
  readonly scheduledDays: number;
  /** Business days on requests awaiting a decision. */
  readonly pendingDays: number;
  /** Days carried over from prior periods that are about to prescribe. */
  readonly expiringDays: number;
  /** When `expiringDays` prescribes. Null when nothing is expiring. */
  readonly expiresOn: CalendarDate | null;
  /** Stored so the "saldo en riesgo" query can order on it server-side. */
  readonly remainingDays: number;
}

/**
 * Days genuinely still available: entitlement less everything already committed.
 * Pending requests count against the balance — otherwise a person could file
 * overlapping requests that each look affordable in isolation.
 */
export const availableDays = (
  balance: Pick<
    Balance,
    'entitledDays' | 'openingUsedDays' | 'takenDays' | 'scheduledDays' | 'pendingDays'
  >,
): number =>
  Math.max(
    0,
    balance.entitledDays -
      balance.openingUsedDays -
      balance.takenDays -
      balance.scheduledDays -
      balance.pendingDays,
  );

/**
 * The same subtraction without the floor.
 *
 * `availableDays` clamps at zero because no screen should offer negative days to
 * spend. The overdraft is real all the same — somebody who took more than they
 * had — and it has to survive to the next period, so the rollover needs to see
 * the true figure rather than the presentable one.
 */
export const netDays = (
  balance: Pick<
    Balance,
    'entitledDays' | 'openingUsedDays' | 'takenDays' | 'scheduledDays' | 'pendingDays'
  >,
): number =>
  balance.entitledDays -
  balance.openingUsedDays -
  balance.takenDays -
  balance.scheduledDays -
  balance.pendingDays;

/**
 * An accumulated balance large enough that HR should intervene. The handoff
 * paints the figure red past this threshold: unused days are a liability the
 * company must eventually pay out, and days that prescribe are lost to the
 * employee.
 */
export const DEFAULT_RISK_THRESHOLD_DAYS = 12;

export const isAtRisk = (
  balance: Pick<Balance, 'remainingDays'>,
  threshold = DEFAULT_RISK_THRESHOLD_DAYS,
): boolean => balance.remainingDays > threshold;

/** Whether the "días por vencer" banner should show. */
export const hasExpiringDays = (
  balance: Pick<Balance, 'expiringDays' | 'expiresOn'>,
): boolean => balance.expiringDays > 0 && balance.expiresOn !== null;
